/**
 * Помощни функции за полетата с текстов редактор (TextEditor).
 *
 * Редакторът записва HTML. Полетата обаче съществуват отпреди него и в базата
 * има запазен обикновен текст с нови редове - затова стойността се разпознава
 * и се преобразува при показване.
 */

// Разпознава HTML по наличието на таг. Достатъчно е за нашия случай: текстът
// или идва от редактора (винаги с тагове), или е стар обикновен текст.
const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*>/i;

export const isHtml = (value: string): boolean => HTML_TAG_PATTERN.test(value);

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Превръща обикновен текст в HTML: празен ред разделя абзаци, единичният нов
 * ред остава нов ред в същия абзац.
 */
export const plainTextToHtml = (value: string): string =>
  value
    .split(/\n{2,}/)
    .map(paragraph => paragraph.trim())
    .filter(Boolean)
    .map(paragraph => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('');

/**
 * Премахва изпълним код от HTML-а, преди да бъде поставен в страницата.
 * Съдържанието се пише от администратор, но <img onerror="..."> и подобни не
 * бива да могат да се промъкнат през редактора (напр. при поставяне отвън).
 */
export const sanitizeHtml = (html: string): string => {
  const template = document.createElement('template');
  template.innerHTML = html;

  template.content.querySelectorAll('script, style, iframe, object, embed, link').forEach(node =>
    node.remove()
  );

  template.content.querySelectorAll('*').forEach(element => {
    Array.from(element.attributes).forEach(attr => {
      const name = attr.name.toLowerCase();
      const value = attr.value.replace(/\s+/g, '').toLowerCase();
      if (name.startsWith('on') || value.startsWith('javascript:')) {
        element.removeAttribute(attr.name);
      }
    });
  });

  return template.innerHTML;
};

/**
 * Стойността, готова за показване на публичния сайт - стар обикновен текст се
 * преобразува в абзаци, а HTML от редактора се изчиства от изпълним код.
 */
export const toDisplayHtml = (value: string): string =>
  isHtml(value) ? sanitizeHtml(value) : plainTextToHtml(value);

/**
 * Само текстът, без таговете. Използва се за кратките редове в списъците на
 * админ панела, където форматирането не се показва.
 */
export const htmlToPlainText = (value: string): string => {
  if (!isHtml(value)) return value;
  const template = document.createElement('template');
  template.innerHTML = value.replace(/<\/(p|div|li|h[1-6])>/gi, ' ').replace(/<br\s*\/?>/gi, ' ');
  return (template.content.textContent || '').replace(/\s+/g, ' ').trim();
};

/**
 * Празно ли е полето - редакторът оставя <p><br></p> и подобни, които не са
 * празен низ, но нямат съдържание. Използва се при проверките преди запазване.
 */
export const isRichTextEmpty = (value: string): boolean =>
  htmlToPlainText(value).trim().length === 0;
